/**
 * **Log the call?** — `docs/activity.md` §11.5, Board 14 C.
 *
 * **Call** hands the number to the phone app, which takes the page away.
 * When the page is visible again within 30 minutes, a sheet asks how it
 * went: the purple call tile, *Log the call?* / *back from the phone app*,
 * *You called Renee Castillo at 9:44 AM · 2 min* (the minutes between
 * leaving and coming back), **Reached · Left a voicemail · No answer · Wrong
 * number** (one choice, the selected chip green), a note with Dictate, where
 * it saves to, and **Skip** · **Save the call**. Skip saves nothing.
 *
 * Saved as a `call` activity — `duration_minutes`, `outcome` in the chip's
 * words, the note as its body — through the upload queue (§11.4) with a
 * client id, so a call logged with no signal is not lost and never doubled.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { RecordRef } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Dictation } from '../../components/dictation.js'
import { Glyph } from '../glyphs.js'
import { uploadQueue } from '../scope/upload-queue.js'
import { SavingTo, type SaveChip } from '../scope/saving-to.js'
import { SheetAction, StopSheet } from './sheet.js'

export const CALL_OUTCOMES = ['Reached', 'Left a voicemail', 'No answer', 'Wrong number'] as const
export type CallOutcome = (typeof CALL_OUTCOMES)[number]

/** §11.5: back within half an hour is coming back from that call. */
export const CALL_RETURN_MINUTES = 30

export interface PlacedCall {
  /** When the page went to the phone app. */
  leftAt: number
  /** When it came back. */
  backAt: number
}

/**
 * Remembers that **Call** was pressed and answers, once, when the page is
 * visible again — within 30 minutes, or not at all.
 */
export function useCallReturn(now: () => number = () => Date.now()): {
  called: () => void
  back: PlacedCall | null
  clear: () => void
} {
  const left = useRef<number | null>(null)
  const [back, setBack] = useState<PlacedCall | null>(null)

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || left.current === null) return
      const at = now()
      const leftAt = left.current
      left.current = null
      if (at - leftAt <= CALL_RETURN_MINUTES * 60_000) setBack({ leftAt, backAt: at })
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [now])

  const called = useCallback(() => { left.current = now() }, [now])
  const clear = useCallback(() => setBack(null), [])
  return { called, back, clear }
}

/** *9:44 AM*, on this phone's clock. */
export function callTime(at: number): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(at))
}

export function callMinutes(call: PlacedCall): number {
  return Math.max(1, Math.round((call.backAt - call.leftAt) / 60_000))
}

export function LogCallSheet({
  name, call, refs, chips, madeFromActivityId, onClose, onSaved,
}: {
  /** *Renee Castillo*. */
  name: string
  call: PlacedCall
  /** Every record it is about, the first being where it is written. */
  refs: RecordRef[]
  chips: SaveChip[]
  madeFromActivityId?: string
  onClose: () => void
  onSaved?: () => void
}) {
  const [outcome, setOutcome] = useState<CallOutcome | null>(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const minutes = callMinutes(call)

  const save = async () => {
    const [first, ...rest] = refs
    if (!first || saving) return
    setSaving(true)
    await uploadQueue().addActivity({
      batch: 'calls',
      record: first,
      payload: {
        kind: 'call', durationMinutes: minutes, outcome: outcome ?? null,
        /* When the call was placed, not when it is saved (§10.3). */
        occurredAt: new Date(call.leftAt).toISOString(),
        ...(note.trim() ? { body: note.trim() } : {}),
        links: rest, expand: false,
        ...(madeFromActivityId ? { madeFromActivityId } : {}),
      },
      fileClientIds: [],
    })
    onSaved?.()
    onClose()
  }

  return (
    <StopSheet
      title="Log the call?"
      sub="back from the phone app"
      tile={(
        <span className={cn('inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] border',
          'border-purple-line')}
          style={{ background: 'rgb(var(--status-job-invoiced-bg))', color: 'rgb(var(--status-job-invoiced-fg))' }}>
          <Glyph name="phone" size={17} stroke={1.9} />
        </span>
      )}
      onClose={onClose}
      minHeight={580}
      footer={(
        <>
          <SheetAction onClick={onClose}>Skip</SheetAction>
          <SheetAction primary disabled={saving} onClick={() => void save()} icon={<Glyph name="check" size={16} />}>
            Save the call
          </SheetAction>
        </>
      )}
    >
      <div className="mb-2.5 text-[13px] text-text-muted">
        You called {name} at {callTime(call.leftAt)} · {minutes} min
      </div>
      <div className="mb-[5px] text-[11.5px] font-medium text-text-muted">How did it go?</div>
      <div role="radiogroup" aria-label="How did it go?" className="flex flex-wrap gap-1.5">
        {CALL_OUTCOMES.map((choice) => {
          const on = outcome === choice
          return (
            <button key={choice} type="button" role="radio" aria-checked={on}
              onClick={() => setOutcome(on ? null : choice)}
              className={cn('inline-flex h-10 items-center gap-[5px] rounded-full border px-3.5 text-[13.5px] font-semibold',
                on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text')}>
              {on && <Glyph name="check" size={13} stroke={2.6} />}
              {choice}
            </button>
          )
        })}
      </div>
      <div className="mb-[5px] mt-3.5 text-[11.5px] font-medium text-text-muted">Note</div>
      <div className={cn('rounded-xl border border-line bg-surface',
        'focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]')}>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          aria-label="Note"
          rows={3}
          className="block min-h-[70px] w-full resize-none rounded-xl bg-transparent px-3 py-2.5 text-[14px] leading-[1.5] outline-none focus-visible:shadow-none"
        />
        <div className="flex min-h-[44px] items-center gap-2 border-t border-line-faint p-2">
          <Dictation variant="chip" onText={(said) => setNote((was) => (was ? `${was} ${said}` : said))} />
          <span className="flex-1" />
        </div>
      </div>
      <SavingTo word="Saves to" chips={chips} large className="mt-3" />
    </StopSheet>
  )
}
